import { useState, useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';

import {
  useI18n,
  ComboBox,
  RadioButtonGroup,
  RadioButton,
  useAfterInitialEffect
} from '@pega/cosmos-react-core';
import type { MenuItemProps } from '@pega/cosmos-react-core';

import type { PostVisibilityControlProps } from './Feed.types';

const FeedVisibilityControls = ({
  visibilityMode,
  visibility,
  setVisibility,
  recipientInput
}: PostVisibilityControlProps) => {
  const t = useI18n();

  const {
    availableRecipients,
    recipients,
    onAdd,
    onRemove,
    onToggle,
    onSearch,
    onMenuOpen,
    loadMore,
    loading
  } = recipientInput;

  const [searchValue, setSearchValue] = useState('');

  const itemsToRender = useMemo(() => {
    return availableRecipients.map(item => ({
      ...item,
      selected: recipients.some(r => r.id === item.id)
    }));
  }, [availableRecipients, recipients]);

  const toggle = (itemId: MenuItemProps['id']) => {
    const recipient = itemsToRender.find(item => item.id === itemId);
    if (!recipient || recipient.selected) {
      onRemove(itemId);
    } else {
      onAdd(itemId);
    }
    onToggle?.(itemId);
    setSearchValue('');
  };

  useAfterInitialEffect(() => {
    if (visibility !== 'private') {
      recipients.forEach(item => onRemove(item.id));
    }
  }, [visibility]);

  useEffect(() => {
    onSearch(searchValue);
  }, [searchValue]);

  return (
    <>
      {visibilityMode === 'selectable' && (
        <RadioButtonGroup name='visibility' label={t('feed_mode')} required inline>
          <RadioButton
            label={t('feed_public')}
            checked={visibility === 'public'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                setVisibility?.('public');
              }
            }}
          />
          <RadioButton
            label={t('feed_private')}
            checked={visibility === 'private'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.checked) {
                setVisibility?.('private');
              }
            }}
          />
        </RadioButtonGroup>
      )}

      {visibility === 'private' && (
        <ComboBox
          mode='multi-select'
          label={t('feed_users')}
          required
          selected={{
            items: recipients.map(item => ({ text: item.primary, id: item.id })),
            onRemove: toggle
          }}
          value={searchValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchValue(e.target.value);
          }}
          onBlur={() => {
            setSearchValue('');
          }}
          onMenuOpen={onMenuOpen}
          menu={
            searchValue
              ? {
                  items: itemsToRender,
                  onItemClick: toggle,
                  emptyText: t('no_items'),
                  loadMore,
                  loading
                }
              : {
                  items: [],
                  emptyText: t('combobox_search_instructions')
                }
          }
        />
      )}
    </>
  );
};

export default FeedVisibilityControls;
